import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

/**
 * Generate a consistent session key for a given video and language combination
 * Can be optionally extended with a section ID for section-specific sessions
 * 
 * @param {string} videoId - The ID of the video
 * @param {string} languageName - The language name
 * @param {string} [sectionId] - Optional section ID for section editing sessions
 * @returns {string} A consistent session key
 */
export const generateSessionKey = (videoId, languageName, sectionId = null) => {
    if (sectionId) {
        return `edit_session_${videoId}_${languageName}_section_${sectionId}`;
    }
    return `edit_session_${videoId}_${languageName}`;
};

/**
 * Generate a similar key pattern for read-only status
 * 
 * @param {string} videoId - The ID of the video
 * @param {string} languageName - The language name
 * @param {string} [sectionId] - Optional section ID for section editing sessions
 * @returns {string} A consistent read-only key
 */
export const generateReadOnlyKey = (videoId, languageName, sectionId = null) => {
    if (sectionId) {
        return `read_only_${videoId}_${languageName}_section_${sectionId}`;
    }
    return `read_only_${videoId}_${languageName}`;
};

class EditSessionService {
    generateSessionId() {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }

    
    
    /**
     * Start an edit session for a video language
     */
    async startEditSession(videoId, languageName, email, authToken) {
        try {
            // Get or create session ID for this video/language combination
            const sessionKey = generateSessionKey(videoId, languageName);
            let sessionId = sessionStorage.getItem(sessionKey); // Use sessionStorage instead of localStorage
            
            if (!sessionId) {
                sessionId = this.generateSessionId();
                console.log(`Starting new edit session for ${sessionKey}: ${sessionId}`);
                sessionStorage.setItem(sessionKey, sessionId);
            }
            
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || ''}/api/v3/videos/${videoId}/languages/${languageName}/edit-session/start`,
                {},
                {
                    headers: {
                        email: email,
                        auth: authToken
                    },
                    params: {
                        sessionId: sessionId
                    }
                }
            );
            
            // Handle different status responses
            if (response.status === 200) {
                if (response.data.sessionId) {
                    // Store the session ID returned by the server
                    console.log(`Edit session started successfully for ${sessionKey}: ${response.data.sessionId}`);
                    sessionStorage.setItem(sessionKey, response.data.sessionId);
                }
            }
            
            return response.data;
        } catch (error) {
            // Handle 409 Conflict specially for duplicate sessions
            if (error.response && error.response.status === 409) {
                const sessionKey = generateSessionKey(videoId, languageName);
                if (error.response.data.sessionId) {
                    // Store the "real" session ID for future reference
                    console.warn(`Duplicate session detected for ${sessionKey}: ${error.response.data.sessionId}`);
                    sessionStorage.setItem(sessionKey, error.response.data.sessionId);
                }
                return error.response.data;
            }
            console.error('Error starting edit session:', error);
            throw error;
        }
    }

    /**
     * Keep the edit session alive with periodic updates
     */
    async keepAliveEditSession(videoId, languageName, email, authToken) {
        try {
            const sessionKey = generateSessionKey(videoId, languageName);
            const sessionId = sessionStorage.getItem(sessionKey);
            
            if (!sessionId) {
                console.log(`No session ID found for keep-alive request  ${sessionKey}`);
                return { status: 'NO_SESSION' };
            }
            
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL || ''}/api/v3/videos/${videoId}/languages/${languageName}/edit-session/keep-alive`,
                {},
                {
                    headers: {
                        email: email,
                        auth: authToken
                    },
                    params: {
                        sessionId: sessionId
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error keeping edit session alive:', error);
            throw error;
        }
    }

    /**
     * End an edit session
     */
    async endEditSession(videoId, languageName, email, authToken) {
        const sessionKey = generateSessionKey(videoId, languageName);
        const sessionId = sessionStorage.getItem(sessionKey);
        
        // Always clean up the local session storage regardless of API call result
        console.log(`Ending edit session for ${sessionKey}: ${sessionId}`);
        sessionStorage.removeItem(sessionKey);
        
        // If no session ID, just return early
        if (!sessionId) {
            console.warn(`No session ID found for end session request ${sessionKey}`);
            return { status: 'NO_SESSION' };
        }
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL || ''}/api/v3/videos/${videoId}/languages/${languageName}/edit-session/end`,
                {},
                {
                    headers: {
                        email: email,
                        auth: authToken
                    },
                    params: {
                        sessionId: sessionId
                    }
                }
            );
            
            return response.data;
        } catch (error) {
            console.error('Error ending edit session:', error);
            // Even though the API call failed, we've already cleaned up the local session
            return { status: 'CLEANUP_ONLY' };
        }
    }

    /**
     * Clean up a local session without calling the API
     */
    cleanupLocalSession(videoId, languageName) {
        const sessionKey = generateSessionKey(videoId, languageName);
        console.log(`Cleaning up local session for ${sessionKey}`);
        sessionStorage.removeItem(sessionKey);
        return { status: 'CLEANUP_ONLY' };
    }

    /**
     * Mark a session as read-only (for duplicate sessions)
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @param {string} sessionId - The session ID
     */
    markSessionAsReadOnly(videoId, languageName, sessionId) {
        const readOnlyKey = `read_only_${videoId}_${languageName}`;
        sessionStorage.setItem(readOnlyKey, 'true');
    }

    /**
     * Check if current session is read-only
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @returns {boolean} - Whether this is a read-only session
     */
    isReadOnlySession(videoId, languageName) {
        const readOnlyKey = `read_only_${videoId}_${languageName}`;
        return sessionStorage.getItem(readOnlyKey) === 'true';
    }
    
    /**
     * Check if there's already a session for this video/language in another tab
     * @param {string} videoId - The video ID
     * @param {string} languageName - The language name
     * @returns {Promise<Object>} - Object with exists and sessionId properties
     */
    async checkForExistingSession(videoId, languageName) {
        return new Promise((resolve) => {
            // Generate a unique key for the channel based on video ID and language
            const channel = new BroadcastChannel(`vaani-session-check-${videoId}-${languageName}`);
            
            // Set up listener for responses
            channel.onmessage = (event) => {
                if (event.data.hasSession) {
                    resolve({ exists: true, sessionId: event.data.sessionId });
                    channel.close();
                }
            };
            
            // Send a message to check for existing sessions
            channel.postMessage({ checkSession: true });
            
            // Set a timeout to resolve if no responses are received
            setTimeout(() => {
                resolve({ exists: false });
                channel.close();
            }, 500); // Give 500ms for other tabs to respond
        });
    }

    // End edit session using beacon API for page unload
    endEditSessionWithBeacon(videoId, language) {
        try {
            const sessionKey = generateSessionKey(videoId, language);
            const sessionId = sessionStorage.getItem(sessionKey);
            
            if (!sessionId) {
                console.warn('No session ID found for endEditSessionWithBeacon');
                return false;
            }
            
            // Get auth data from localStorage
            const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
            const authToken = JSON.parse(localStorage.getItem('authToken') || '""');
            
            // Create a FormData object for the beacon payload
            const data = new FormData();
            data.append('sessionId', sessionId);
            data.append('email', authUser.email);
            data.append('auth', authToken);
            
            // SendBeacon only accepts two arguments: URL and data
            const result = navigator.sendBeacon(
                `${process.env.REACT_APP_API_URL || ''}/api/v3/videos/${videoId}/languages/${language}/edit-session/end-beacon`,
                data
            );
            
            
            return result;
        } catch (error) {
            console.error('Error with sendBeacon for ending session:', error);
            // Still clean up the session storage
            const sessionKey = generateSessionKey(videoId, language);
            sessionStorage.removeItem(sessionKey);
            return false;
        }
    }
}

export default new EditSessionService();